import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../lib/api.js";
import * as storage from "../lib/storage.js";
import type { StoredList } from "../types/index.js";
import styles from "./SyncSettings.module.css";

type SyncState =
  | { status: "idle" }
  | { status: "generating"; syncCode: string; expiresAt: Date; syncAccountId: string }
  | { status: "entering" }
  | { status: "synced" }
  | { status: "error"; message: string };

interface Props {
  syncAccountId: string | null;
  lists: StoredList[];
  displayName: string;
  onSyncChange: (syncAccountId: string | null) => void;
}

function formatCode(code: string): string {
  if (code.length <= 4) return code;
  return `${code.slice(0, 4)} ${code.slice(4)}`;
}

export function SyncSettings({ syncAccountId, lists, displayName, onSyncChange }: Props) {
  const [state, setState] = useState<SyncState>(
    syncAccountId ? { status: "synced" } : { status: "idle" },
  );
  const [inputCode, setInputCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);

  // Countdown timer for code expiry
  useEffect(() => {
    if (state.status !== "generating") return;

    const updateRemaining = () => {
      const remaining = Math.max(0, Math.ceil((state.expiresAt.getTime() - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        setState({ status: "idle" });
      }
    };

    updateRemaining();
    timerRef.current = setInterval(updateRemaining, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state]);

  const handleGenerate = useCallback(async () => {
    setState({ status: "idle" });
    try {
      const result = await api.syncCreate(displayName, lists);
      storage.setSyncAccountId(result.syncAccountId);
      onSyncChange(result.syncAccountId);
      setState({
        status: "generating",
        syncCode: result.syncCode,
        expiresAt: new Date(result.expiresAt),
        syncAccountId: result.syncAccountId,
      });
    } catch (err) {
      console.error("Failed to create sync:", err);
      setState({ status: "error", message: "同期コードの生成に失敗しました。" });
    }
  }, [displayName, lists, onSyncChange]);

  const handleLink = useCallback(async () => {
    const code = inputCode.replace(/\s/g, "").toUpperCase();
    if (code.length !== 8) return;

    setSubmitting(true);
    try {
      const result = await api.syncLink(code, displayName, lists);
      storage.setSyncAccountId(result.syncAccountId);
      storage.applyRemoteSync({
        displayName: result.mergedData.displayName,
        lists: result.mergedData.lists,
      });
      onSyncChange(result.syncAccountId);
      setState({ status: "synced" });
      setInputCode("");
    } catch (err) {
      console.error("Failed to link sync:", err);
      const message =
        err instanceof api.ApiError && err.code === "NOT_FOUND"
          ? "同期コードが見つかりません。"
          : "同期に失敗しました。コードを確認してください。";
      setState({ status: "error", message });
    } finally {
      setSubmitting(false);
    }
  }, [inputCode, displayName, lists, onSyncChange]);

  const handleDisconnect = useCallback(() => {
    storage.clearSyncAccountId();
    onSyncChange(null);
    setState({ status: "idle" });
  }, [onSyncChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only valid sync code characters, auto-uppercase
    const raw = e.target.value
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 8);
    setInputCode(raw);
  }, []);

  if (state.status === "synced" || (syncAccountId && state.status === "idle")) {
    return (
      <section className={styles.container}>
        <div className={styles.syncedRow}>
          <span className={styles.syncedIndicator}>同期中</span>
          <button type="button" className={styles.disconnectButton} onClick={handleDisconnect}>
            同期を解除
          </button>
        </div>
      </section>
    );
  }

  if (state.status === "generating") {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return (
      <section className={styles.container}>
        <p className={styles.label}>同期コード</p>
        <p className={styles.code}>{formatCode(state.syncCode)}</p>
        <p className={styles.expiry}>
          有効期限: {minutes}:{seconds.toString().padStart(2, "0")}
        </p>
        <p className={styles.hint}>別の端末でこのコードを入力してください</p>
      </section>
    );
  }

  if (state.status === "entering") {
    return (
      <section className={styles.container}>
        <div className={styles.enterForm}>
          <input
            type="text"
            value={formatCode(inputCode)}
            onChange={handleInputChange}
            placeholder="同期コード"
            maxLength={9}
            className={styles.codeInput}
            autoComplete="off"
            autoCapitalize="characters"
          />
          <button
            type="button"
            onClick={handleLink}
            disabled={inputCode.length !== 8 || submitting}
            className={styles.submitButton}
          >
            接続
          </button>
        </div>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={() => {
            setState({ status: "idle" });
            setInputCode("");
          }}
        >
          キャンセル
        </button>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      {state.status === "error" && (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      )}
      <div className={styles.buttonRow}>
        <button type="button" className={styles.actionButton} onClick={handleGenerate}>
          同期コードを生成
        </button>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => setState({ status: "entering" })}
        >
          同期コードを入力
        </button>
      </div>
    </section>
  );
}
